"use client";

import { useState, useRef, useEffect } from "react";
import { Bell, ExternalLink, CheckCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useLocale, useTranslations } from "next-intl";
import { ar, enUS } from "date-fns/locale";
import Link from "next/link";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import { useNotifications } from "@/modules/dashboard/context/NotificationContext";
import { getCategoryIcon } from "@/modules/dashboard/components/notificationCategoryIcons";
import { getRegionLocaleHref } from "@/services/region";
import { usePathname } from "next/navigation";

export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileTop, setMobileTop] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const locale = useLocale();
  const pathname = usePathname();
  const t = useTranslations("web.navbar.notifications");
  const { user } = useCurrentUser();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();

  const accountTypePrefix = user?.account_type === "teacher" ? "t" : "s";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 639px)");
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);
    updateIsMobile();
    mediaQuery.addEventListener("change", updateIsMobile);
    return () => mediaQuery.removeEventListener("change", updateIsMobile);
  }, []);

  const handleToggle = () => {
    if (!isOpen && isMobile && buttonRef.current) {
      setMobileTop(buttonRef.current.getBoundingClientRect().bottom + 8);
    }
    setIsOpen((prev) => !prev);
  };

  const handleMarkAsRead = async (id: string, link?: string) => {
    await markAsRead(id);
    if (link) {
      setIsOpen(false);
    }
  };

  const handleMarkAllAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await markAllAsRead();
  };

  const dateLocale = locale === "ar" ? ar : enUS;

  const getLocalizedContent = (data: { en?: string; ar?: string } | undefined) => {
    if (!data) return "";
    return locale === "ar" ? (data.ar || data.en) : (data.en || data.ar);
  };

  const getCategoryLabel = (category?: string) => t(`categories.${category ?? "system"}`);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground transition-all duration-200 hover:bg-hover hover:-translate-y-0.5 cursor-pointer"
      >
        <Bell size={18} className="text-foreground" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-secondary text-on-secondary text-[10px] px-1 rounded-full min-w-[16px] text-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          style={isMobile ? { top: `${mobileTop}px` } : undefined}
          className={`bg-surface-elevated border border-border rounded-lg shadow-xl z-50 ${
            isMobile
              ? "fixed left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] max-w-[22rem]"
              : `absolute top-full mt-2 w-80 ${locale === "ar" ? "left-0" : "right-0"}`
          }`}
        >
          <div className="p-3 border-b border-border flex justify-between items-center">
            <h3 className="font-semibold text-foreground text-sm">
              {t("title")}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-[11px] text-secondary hover:underline flex items-center gap-1"
              >
                <CheckCircle size={12} />
                {t("mark_all_read")}
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="p-4 text-center text-sm text-text-muted">
                {t("loading")}
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-sm text-text-muted">
                {t("no_notifications")}
              </div>
            ) : (
              notifications.slice(0, 5).map((n) => {
                const title = getLocalizedContent(n.data.title);
                const message = getLocalizedContent(n.data.message);
                const CategoryIcon = getCategoryIcon(n.data.category);
                const isHighPriority = n.data.priority === "high";

                return (
                  <div
                    key={n.id}
                    className={`p-3 border-b border-border-subtle last:border-0 hover:bg-hover transition-colors duration-150 cursor-pointer ${
                      !n.read_at ? "bg-status-info/5" : ""
                    }`}
                    onClick={() => !n.read_at && handleMarkAsRead(n.id)}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1">
                        {title && (
                          <h4 className="text-xs font-bold text-foreground mb-1 flex items-center gap-1">
                            <span
                              className="shrink-0 inline-flex"
                              title={getCategoryLabel(n.data.category)}
                            >
                              <CategoryIcon size={12} className="text-secondary" />
                            </span>
                            {title}
                          </h4>
                        )}
                        <p className="text-xs text-text-secondary leading-relaxed">
                          {message}
                        </p>
                        <span className="text-[10px] text-text-muted mt-2 block">
                          {formatDistanceToNow(new Date(n.created_at), {
                            addSuffix: true,
                            locale: dateLocale,
                          })}
                        </span>
                      </div>
                      {!n.read_at && (
                        <div
                          className={`w-2 h-2 rounded-full mt-1 shrink-0 ${
                            isHighPriority ? "bg-danger" : "bg-secondary"
                          }`}
                        />
                      )}
                    </div>

                    {n.data.link && (
                      <Link
                        href={n.data.link}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(n.id, n.data.link);
                        }}
                        className="mt-2 text-[11px] text-secondary flex items-center gap-1 hover:underline"
                      >
                        <ExternalLink size={10} />
                        {t("view_details")}
                      </Link>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="p-2 border-t border-border text-center">
             <Link
               href={getRegionLocaleHref(pathname, locale, `/${accountTypePrefix}/notifications`)}
               onClick={() => setIsOpen(false)}
               className="text-[11px] text-text-muted hover:text-foreground transition-colors duration-150"
             >
                {t("view_all")}
             </Link>
          </div>
        </div>
      )}
    </div>
  );
}
